import { PlansType, SubscriptionsType } from 'components/pages/companies/billing/billing-plans-context'
import { PicklePlan } from 'interfaces/pickle-plan'
import { PickleSubscription } from 'interfaces/pickle-subscription'

export function platformBasePlan(plans: PicklePlan[]) {
  return plans.find((plan) => plan.permalink === 'platform_base')
}

export function platformProPlan(plans: PicklePlan[]) {
  return plans.find((plan) => plan.permalink === 'platform_pro')
}

export function creativePlan(plans: PicklePlan[], dailyTime: number) {
  return plans.find((plan) => plan.permalink === 'creative_services' && plan.dailyTime === dailyTime)
}

export function professionalPlan(plans: PicklePlan[], dailyTime: number) {
  return plans.find((plan) => plan.permalink === 'professional_services' && plan.dailyTime === dailyTime)
}

export function displayPriceDifference(price: number, currency: string) {
  if (price < 0) {
    return `-${currencyToSymbol(currency)}${Math.abs(price).toFixed(2)}`
  } else {
    return `+${currencyToSymbol(currency)}${price.toFixed(2)}`
  }
}

export function displayPlanPrice(plan: PicklePlan) {
  const { price, currency } = plan
  return `${currencyToSymbol(currency)}${Number(price).toFixed(2)}/${plan.period}`
}

export function displayPrice(price: number, currency: string) {
  return `${currencyToSymbol(currency)}${(price / 100).toFixed(2)}`
}

export function getTotalPrice(plans: PicklePlan[]) {
  const total = plans.reduce((acc, plan) => {
    if (plan?.price) {
      return acc + Number(plan.price)
    }
    return acc
  }, 0)
  return total
}

export function totalCurrent(currentSubscriptions: SubscriptionsType) {
  return getTotalPrice(
    Object.values([
      currentSubscriptions.platformSubscription,
      currentSubscriptions.creativeSubscription,
      currentSubscriptions.professionalSubscription,
    ])
      .filter(Boolean)
      .map((sub) => sub.picklePlan)
      .filter(Boolean),
  )
}

export function totalSelected(selectedPlans: PlansType) {
  return getTotalPrice(Object.values(selectedPlans))
}

export function displayTotalPrice(plans: PicklePlan[]) {
  const currency = plans[0]?.currency || 'USD'
  const period = plans[0]?.period || 'monthly'

  const total = getTotalPrice(plans)

  return `${currencyToSymbol(currency)}${total.toFixed(2)}/${period}`
}

export function currencyToSymbol(currency: string) {
  switch (currency) {
    case 'USD':
      return '$'
    case 'EUR':
      return '€'
    case 'GBP':
      return '£'
    default:
      return currency
  }
}

export function getSubscriptionItems(currentSubscriptions, selectedPlans) {
  const items = []
  const planTypes = [
    { subKey: 'platformSubscription', planKey: 'platformPlan' },
    { subKey: 'creativeSubscription', planKey: 'creativePlan' },
    { subKey: 'professionalSubscription', planKey: 'professionalPlan' },
  ]
  planTypes.forEach(({ subKey, planKey }) => {
    const currentSub = currentSubscriptions[subKey]
    const selectedPlan = selectedPlans[planKey]
    if (currentSub && selectedPlan) {
      if (currentSub.picklePlan.id !== selectedPlan.id) {
        items.push({ price: selectedPlan.stripePlanId, quantity: 1 })
        items.push({ id: currentSub.stripeItemId, deleted: true })
      }
    } else if (currentSub && !selectedPlan) {
      items.push({ id: currentSub.stripeItemId, deleted: true })
    } else if (!currentSub && selectedPlan) {
      items.push({ price: selectedPlan.stripePlanId, quantity: 1 })
    }
  })
  return items
}

export function getDowngradeSubscriptionItems(selectedPlans: PlansType) {
  const items = []
  const planTypes = [{ planKey: 'platformPlan' }, { planKey: 'creativePlan' }, { planKey: 'professionalPlan' }]
  planTypes.forEach(({ planKey }) => {
    const selectedPlan = selectedPlans[planKey]
    if (selectedPlan) {
      items.push({ price: selectedPlan.stripePlanId, quantity: 1 })
    }
  })
  return items
}

export function mapPickleSubscriptionsToSubscriptionsType(subscriptions: PickleSubscription[]) {
  return subscriptions.reduce((acc, subscription) => {
    if (subscription.picklePlan?.creativeServices) {
      acc.creativeSubscription = subscription
    } else if (subscription.picklePlan?.platform) {
      acc.platformSubscription = subscription
    } else if (subscription.picklePlan?.professionalServices) {
      acc.professionalSubscription = subscription
    }
    return acc
  }, {} as SubscriptionsType)
}
